/**
 * RBAC 契约：**角色 = 功能档位模板**（用户 2026-10-07 裁定）。
 *
 * 裁定要点（见 docs/plan/rbac-and-settings-redesign.md A-1/A-2）：
 *  · 角色给出一套功能档位（`none|read|write`）；**角色不含店铺**（店铺只按用户）；
 *  · ⭐ **有效档位 = 用户覆盖 ?? 角色模板**（用户可覆盖）⇒ 本文件 `effectiveFeatureAccess` 是**唯一实现**；
 *  · 角色作用域 = 全局；内置 `admin|manager|viewer` 不可删/不可改名；可自定义角色。
 *
 * 本模块零依赖（只 import 同包内的 feature-access），可在任何运行时被纯函数测试。
 */
import { FEATURE_POINTS, type AccessLevel } from "./feature-access.js";

/** 角色 id 前缀。 */
export const ROLE_ID_PREFIX = "role_";
/** 角色名长度上限。 */
export const ROLE_NAME_MAX_LENGTH = 64;
/** 内置角色名（不可删、不可改名）——与 DB `roles_builtin_name_chk` 同口径。 */
export const BUILTIN_ROLE_NAMES = ["admin", "manager", "viewer"] as const;
export type BuiltinRoleName = (typeof BUILTIN_ROLE_NAMES)[number];

export type RoleId = string;

/** 角色 id 形状：`role_` + 非空后缀（内置为 `role_admin` 等）。 */
export function parseRoleId(raw: unknown): RoleId | null {
  if (typeof raw !== "string") return null;
  const value = raw.trim();
  if (!value.startsWith(ROLE_ID_PREFIX)) return null;
  if (value.length <= ROLE_ID_PREFIX.length) return null;
  return value;
}

export function isRoleId(raw: unknown): boolean {
  return parseRoleId(raw) !== null;
}

/** 角色 → 功能档位（feature 必须来自 `FEATURE_POINTS`）。 */
export interface RoleFeatureGrant {
  readonly feature: string;
  readonly access: AccessLevel;
}

export interface Role {
  readonly roleId: RoleId;
  readonly name: string;
  readonly isBuiltin: boolean;
  /** 已配置的档位条数（列表页显示用；不参与判定）。 */
  readonly grantCount?: number;
  readonly createdBy: string;
  readonly updatedBy: string;
  readonly createdAtIso: string;
  readonly updatedAtIso: string;
  readonly deletedAtIso: string | null;
}

const FEATURE_KEYS = new Set(FEATURE_POINTS.map((point) => point.key));
const WRITABLE_KEYS = new Set(FEATURE_POINTS.filter((point) => point.writable).map((point) => point.key));
const ACCESS_LEVELS: readonly AccessLevel[] = ["none", "read", "write"];

function isAccessLevel(raw: unknown): raw is AccessLevel {
  return typeof raw === "string" && (ACCESS_LEVELS as readonly string[]).includes(raw);
}

/** 一条 rejection（风格与 user-management / auto-budget-templates 一致）。 */
export interface RoleWriteRejection {
  readonly field: string;
  readonly code: string;
  readonly message: string;
}

export type RoleWriteValidation =
  | { readonly ok: true; readonly value: { readonly name: string; readonly grants: readonly RoleFeatureGrant[] } }
  | { readonly ok: false; readonly rejections: readonly RoleWriteRejection[] };

/**
 * 角色写入校验（新建/改名 + 整组档位）。
 * ⭐ **结构堵死**：`access === "write"` 必须该功能点 `writable: true` —— 否则
 *    "给不可写功能点授 write 档"会在**校验层**被拒绝（而不是等到运行时 403 才发现配错）。
 */
export function validateRoleWriteInput(input: { readonly name: unknown; readonly grants: unknown }): RoleWriteValidation {
  const rejections: RoleWriteRejection[] = [];
  const name = typeof input.name === "string" ? input.name.trim() : "";
  if (name === "") {
    rejections.push({ field: "name", code: "required", message: "角色名不能为空" });
  } else if (name.length > ROLE_NAME_MAX_LENGTH) {
    rejections.push({ field: "name", code: "too_long", message: `角色名不能超过 ${ROLE_NAME_MAX_LENGTH} 个字符` });
  } else if (/[\u0000-\u001f\u007f]/.test(name)) {
    rejections.push({ field: "name", code: "control_char", message: "角色名不能包含控制字符" });
  }

  if (!Array.isArray(input.grants)) {
    rejections.push({ field: "grants", code: "invalid", message: "grants 必须是数组" });
    return rejections.length === 0 ? { ok: true, value: { name, grants: [] } } : { ok: false, rejections };
  }
  const grants: RoleFeatureGrant[] = [];
  const seen = new Set<string>();
  input.grants.forEach((raw, index) => {
    const item = (raw ?? {}) as { readonly feature?: unknown; readonly access?: unknown };
    const feature = typeof item.feature === "string" ? item.feature.trim() : "";
    if (feature === "" || !FEATURE_KEYS.has(feature)) {
      rejections.push({
        field: `grants[${index}].feature`,
        code: "unknown_feature",
        message: `未知功能点「${feature}」`,
      });
      return;
    }
    if (seen.has(feature)) {
      rejections.push({ field: `grants[${index}].feature`, code: "duplicate", message: `功能点「${feature}」重复` });
      return;
    }
    if (!isAccessLevel(item.access)) {
      rejections.push({
        field: `grants[${index}].access`,
        code: "unknown_access",
        message: `档位必须是 ${ACCESS_LEVELS.join(" / ")} 之一`,
      });
      return;
    }
    if (item.access === "write" && !WRITABLE_KEYS.has(feature)) {
      rejections.push({
        field: `grants[${index}].access`,
        code: "not_writable",
        message: `功能点「${feature}」不支持写入档（该功能点不可写）`,
      });
      return;
    }
    seen.add(feature);
    grants.push({ feature, access: item.access });
  });

  if (rejections.length > 0) return { ok: false, rejections };
  return { ok: true, value: { name, grants } };
}

/**
 * 某个功能点的有效档位来自哪里。
 *
 * ⭐ **`super_admin` 必须单独一类**（用户裁定 2026-10-08，见 plan A-4）：
 * 超管的权限**不是**"角色模板/用户覆盖"推导出来的，而且超管的 `role_id` 是内置 `role_admin`
 * —— 内置角色**默认没有档位行** ⇒ 若拿它去跑 `effectiveFeatureAccess` 会得到**全 `none`**，
 * 那会把超管**收紧到零权限**（最糟的一类回归：整站不可用）。⇒ 超管必须**显式**表达为全权，
 * 且有**自己的来源类别**（"需要不同解释的状态必须可区分"）。
 */
export type AccessSource = "override" | "role" | "super_admin";

export interface EffectiveFeatureAccess {
  readonly feature: string;
  readonly access: AccessLevel;
  /** ⭐ 来源必须可见：否则"为什么我不能写"要查两处（用户选了"模板+覆盖"模型的必要代价）。 */
  readonly source: AccessSource;
}

/**
 * ⭐ **有效档位 = 用户覆盖 ?? 角色模板** —— 本函数是**唯一实现**。
 *
 * 纪律：
 *  · **只对契约 `FEATURE_POINTS` 里有定义的功能点输出**（未知 feature 一律忽略 ⇒ 不会凭空多出档位）；
 *  · **fail-closed**：两边都没给 ⇒ `none`；
 *  · 用户覆盖**可以更宽也可以更严**（它是"覆盖"，不是"只能收紧"）——由调用方（端点层）用
 *    `validateGrantAssignment` 的授出上限去限制"能授出多宽"，**不在这里偷偷夹紧**（否则
 *    "判定有效档位"与"校验能否授出"会变成两套规则）。
 */
export function effectiveFeatureAccess(input: {
  readonly roleGrants: readonly RoleFeatureGrant[];
  readonly overrides: readonly RoleFeatureGrant[];
}): readonly EffectiveFeatureAccess[] {
  const roleByFeature = new Map<string, AccessLevel>();
  for (const grant of input.roleGrants) {
    if (FEATURE_KEYS.has(grant.feature) && isAccessLevel(grant.access)) roleByFeature.set(grant.feature, grant.access);
  }
  const overrideByFeature = new Map<string, AccessLevel>();
  for (const grant of input.overrides) {
    if (FEATURE_KEYS.has(grant.feature) && isAccessLevel(grant.access)) overrideByFeature.set(grant.feature, grant.access);
  }
  return FEATURE_POINTS.map((point) => {
    const override = overrideByFeature.get(point.key);
    if (override !== undefined) return { feature: point.key, access: override, source: "override" as const };
    const role = roleByFeature.get(point.key);
    if (role !== undefined) return { feature: point.key, access: role, source: "role" as const };
    return { feature: point.key, access: "none" as const, source: "role" as const };
  });
}

/** 档位来源的中文口径（UI 直接显示，不各写一套）。 */
export function describeAccessSource(source: AccessSource): string {
  if (source === "super_admin") return "超级管理员（全权）";
  return source === "override" ? "按你的单独设置（覆盖角色模板）" : "按角色模板";
}
